import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Calendar, Clock, Send } from "lucide-react";
import { useTenant } from "../contexts/TenantContext";
import { fonts as defaultFonts } from "../styles/theme";
import { fetchPublicEventInfo, submitPublicRegistration } from "../services/api";

// Standalone FGF-branded palette for the public registration page.
const PAGE = {
  bg: "#000925",
  bgGradientEnd: "#000f2d",
  card: "#0a1a3d",
  border: "#1a2744",
  text: "#ffffff",
  textSecondary: "#6b7a94",
  inputBg: "#000f2d",
};

export default function Register() {
  const tenant = useTenant();
  const fonts = tenant.theme?.fonts || defaultFonts;
  const accent = tenant.newsletter?.accentColor || "#4E95D9";

  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const token = searchParams.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPublicEventInfo(eventId, email, token)
      .then((res) => {
        setData(res);
        if (res.prefill) {
          setForm({
            firstName: res.prefill.firstName || "",
            lastName: res.prefill.lastName || "",
            email: res.prefill.email || email,
          });
        } else {
          setForm((prev) => ({ ...prev, email }));
        }
      })
      .catch(() => setData({ valid: false, reason: "error" }))
      .finally(() => setLoading(false));
  }, [eventId, email, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.email.trim()) {
      setError("Email is required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitPublicRegistration(eventId, {
        email: form.email.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        token,
      });
      setSuccess(res);
    } catch (err) {
      setError(err.message || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    fontSize: 14,
    fontFamily: fonts.body,
    border: `1px solid ${PAGE.border}`,
    borderRadius: 8,
    background: PAGE.inputBg,
    color: PAGE.text,
    outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    fontSize: 11,
    color: PAGE.textSecondary,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 6,
    fontFamily: fonts.body,
    fontWeight: 600,
  };

  return (
    <div style={{
      height: "100vh",
      overflowY: "auto",
      background: `linear-gradient(180deg, ${PAGE.bg} 0%, ${PAGE.bgGradientEnd} 100%)`,
      fontFamily: fonts.body,
      color: PAGE.text,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "40px 20px",
    }}>
      {/* Logo header */}
      <div style={{ marginBottom: 48, textAlign: "center" }}>
        <img src={tenant.logo} alt={tenant.name} style={{ height: 160, marginBottom: 20, objectFit: "contain" }} />
        <div style={{
          fontSize: 15, letterSpacing: "0.14em", textTransform: "uppercase",
          color: accent, fontWeight: 700,
        }}>
          {tenant.companyName}
        </div>
      </div>

      {/* Main card */}
      <div style={{
        background: PAGE.card,
        border: `1px solid ${PAGE.border}`,
        borderRadius: 16,
        padding: 40,
        width: "100%",
        maxWidth: 480,
        boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
      }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 32 }}>
            <Loader2 size={24} color={PAGE.textSecondary} className="spin" />          </div>
        ) : !data?.valid ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <XCircle size={40} color="#ef4444" style={{ marginBottom: 16 }} />
            <h2 style={{ fontFamily: fonts.heading, fontSize: 22, marginBottom: 8 }}>
              {data?.reason === "cancelled"
                ? "Webcast Cancelled"
                : data?.reason === "invalid_token"
                  ? "Invitation Not Transferable"
                  : "Invalid Link"}
            </h2>
            <p style={{ color: PAGE.textSecondary, fontSize: 14, maxWidth: 400, margin: "0 auto" }}>
              {data?.reason === "cancelled"
                ? "This webcast has been cancelled. Please contact the organiser for more information."
                : data?.reason === "invalid_token"
                  ? "This invitation was sent to a specific recipient and cannot be used by another email address. If you received this link from a colleague and are interested in attending, please click the button below to request your invitation."
                  : "This registration link is invalid or has expired. Please contact the webcast organiser."}
            </p>
            {data?.reason === "invalid_token" && (
              <InvitationRequestForm eventId={eventId} />
            )}
          </div>
        ) : data.alreadyRegistered ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <CheckCircle2 size={40} color="#10b981" style={{ marginBottom: 16 }} />
            <h2 style={{ fontFamily: fonts.heading, fontSize: 22, marginBottom: 8 }}>Already Registered</h2>
            <p style={{ color: PAGE.textSecondary, fontSize: 14, marginBottom: 20 }}>
              You are already registered for this webcast. Check your email for the Zoom joining link.
            </p>
            {data.event && <EventCard event={data.event} fonts={fonts} accent={accent} />}
          </div>
        ) : success ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <CheckCircle2 size={40} color="#10b981" style={{ marginBottom: 16 }} />
            <h2 style={{ fontFamily: fonts.heading, fontSize: 22, marginBottom: 8 }}>Registration Confirmed!</h2>
            <p style={{ color: PAGE.textSecondary, fontSize: 14, marginBottom: 20 }}>
              You have been registered. A confirmation email with the Zoom link and calendar invite has been sent.
            </p>
            {data.event && <EventCard event={data.event} fonts={fonts} accent={accent} />}
            {success.joinUrl && (
              <a href={success.joinUrl} target="_blank" rel="noopener noreferrer" style={{
                display: "inline-block", marginTop: 20, padding: "12px 32px",
                background: accent, color: "#0a0c10", textDecoration: "none",
                fontSize: 14, fontWeight: 700, borderRadius: 8,
              }}>
                Join Zoom Meeting
              </a>
            )}
          </div>
        ) : (
          <>
            <h2 style={{ fontFamily: fonts.heading, fontSize: 22, marginBottom: 4, textAlign: "center" }}>
              Webcast Registration
            </h2>
            <p style={{ color: PAGE.textSecondary, fontSize: 13, textAlign: "center", marginBottom: 24 }}>
              Confirm your details to register
            </p>

            {data.event && <EventCard event={data.event} fonts={fonts} accent={accent} />}

            <form onSubmit={handleSubmit} style={{ marginTop: 24 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={labelStyle}>First Name</label>
                  <input type="text" value={form.firstName}
                    onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))}
                    placeholder="John" style={inputStyle} autoFocus />
                </div>
                <div>
                  <label style={labelStyle}>Last Name</label>
                  <input type="text" value={form.lastName}
                    onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))}
                    placeholder="Smith" style={inputStyle} />
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Email <span style={{ color: "#ef4444" }}>*</span></label>
                <input type="email" value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  placeholder="john@company.com" style={inputStyle} required />
              </div>

              {error && (
                <div style={{
                  padding: "10px 14px", marginBottom: 16, borderRadius: 6,
                  background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
                  color: "#ef4444", fontSize: 13,
                }}>
                  {error}
                </div>
              )}

              <button type="submit" disabled={submitting} style={{
                width: "100%", padding: "14px 0",
                background: accent,
                color: "#0a0c10", border: "none", borderRadius: 8,
                fontSize: 15, fontWeight: 700, fontFamily: fonts.body,
                cursor: submitting ? "not-allowed" : "pointer",
                opacity: submitting ? 0.7 : 1,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}>
                {submitting && <Loader2 size={16} className="spin" />}
                {submitting ? "Registering..." : "Confirm Registration"}
              </button>
            </form>
          </>
        )}
      </div>

      {/* Footer */}
      <div style={{ marginTop: 40, textAlign: "center", color: PAGE.textSecondary, fontSize: 11 }}>
        &copy; {new Date().getFullYear()} {tenant.legalName} All rights reserved.
      </div>    </div>
  );
}

function EventCard({ event, fonts, accent }) {
  const eventDate = new Date(event.scheduledAt);
  return (
    <div style={{
      background: PAGE.card,
      border: `1px solid ${PAGE.border}`,
      borderRadius: 10,
      padding: "20px 24px",
    }}>
      <div style={{ fontSize: 17, fontWeight: 700, fontFamily: fonts.heading, marginBottom: 10 }}>
        {event.title}
      </div>
      {event.description && (
        <p style={{ fontSize: 13, color: PAGE.textSecondary, marginBottom: 10, lineHeight: 1.5 }}>
          {event.description}
        </p>
      )}
      <div style={{ display: "flex", gap: 16, fontSize: 13, color: accent }}>
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Calendar size={13} />
          {eventDate.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Clock size={13} />
          {eventDate.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} UTC
          {event.durationMinutes && ` (${event.durationMinutes}m)`}
        </span>
      </div>
    </div>
  );
}

function InvitationRequestForm({ eventId }) {
  const [showForm, setShowForm] = useState(false);
  const [reqForm, setReqForm] = useState({ email: "", firstName: "", lastName: "", company: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reqForm.email || !reqForm.firstName || !reqForm.lastName) return;
    setSubmitting(true);
    try {
      const res = await fetch("/api/zoom-invitation-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ eventId, ...reqForm }),
      });
      const data = await res.json();
      setResult(data);
    } catch {
      setResult({ success: false, message: "Something went wrong. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  if (result?.success) {
    return (
      <div style={{ marginTop: 24, padding: 20, background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)", borderRadius: 8 }}>
        <CheckCircle2 size={24} color="#10b981" style={{ marginBottom: 8 }} />
        <p style={{ color: "#10b981", fontSize: 14, margin: 0 }}>{result.message}</p>
      </div>
    );
  }

  if (!showForm) {
    return (
      <button
        onClick={() => setShowForm(true)}
        style={{
          marginTop: 24,
          padding: "12px 28px",
          background: "#4E95D9",
          color: "#ffffff",
          border: "none",
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 700,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Send size={14} /> Request Invitation
      </button>
    );
  }

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    background: PAGE.card,
    border: `1px solid ${PAGE.border}`,
    borderRadius: 6,
    color: PAGE.text,
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 24, textAlign: "left", maxWidth: 360, margin: "24px auto 0" }}>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", fontSize: 12, color: PAGE.textSecondary, marginBottom: 4 }}>First Name *</label>
        <input type="text" required value={reqForm.firstName} onChange={(e) => setReqForm(f => ({ ...f, firstName: e.target.value }))} style={inputStyle} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", fontSize: 12, color: PAGE.textSecondary, marginBottom: 4 }}>Last Name *</label>
        <input type="text" required value={reqForm.lastName} onChange={(e) => setReqForm(f => ({ ...f, lastName: e.target.value }))} style={inputStyle} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", fontSize: 12, color: PAGE.textSecondary, marginBottom: 4 }}>Email *</label>
        <input type="email" required value={reqForm.email} onChange={(e) => setReqForm(f => ({ ...f, email: e.target.value }))} style={inputStyle} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 12, color: PAGE.textSecondary, marginBottom: 4 }}>Company</label>
        <input type="text" value={reqForm.company} onChange={(e) => setReqForm(f => ({ ...f, company: e.target.value }))} style={inputStyle} />
      </div>
      {result && !result.success && (
        <p style={{ color: "#ef4444", fontSize: 13, marginBottom: 12 }}>{result.message || result.error}</p>
      )}
      <button
        type="submit"
        disabled={submitting}
        style={{
          width: "100%",
          padding: "12px",
          background: "#4E95D9",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 700,
          cursor: submitting ? "wait" : "pointer",
          opacity: submitting ? 0.7 : 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
      >
        {submitting ? <><Loader2 size={14} className="spin" /> Submitting...</> : <><Send size={14} /> Submit Request</>}
      </button>
    </form>
  );
}
